/**
 * Payments Section for Assistant Profile
 * Allows upload of W9 and setting of payment method preferences
 * W9 and payment info are only visible to studios AFTER hiring
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, Upload, Check, DollarSign, CreditCard, Loader2, X } from "lucide-react";

const PAYMENT_METHODS = [
  { value: "paypal", label: "PayPal", placeholder: "PayPal email address" },
  { value: "venmo", label: "Venmo", placeholder: "@username" },
  { value: "cashapp", label: "Cash App", placeholder: "$cashtag" },
  { value: "zelle", label: "Zelle", placeholder: "Phone number or email" },
];

export default function PaymentsSection({ 
  w9Uploaded,
  setW9Uploaded,
  w9FileName,
  setW9FileName,
  paymentMethods,
  setPaymentMethods,
  token,
  API
}) {
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const processFile = async (file) => {
    if (!file) return;

    // Validate file type (JPG, PNG, or PDF)
    const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, or PDF of your W9");
      return;
    }

    // Validate file size (max 10MB for PDFs, 5MB for images)
    const maxSize = file.type === "application/pdf" ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(file.type === "application/pdf" ? "PDF must be less than 10MB" : "Image must be less than 5MB");
      return;
    }

    // Validate file header (magic bytes) to prevent renamed malicious files
    const headerBytes = await file.slice(0, 4).arrayBuffer();
    const header = new Uint8Array(headerBytes);
    const isJPEG = header[0] === 0xFF && header[1] === 0xD8 && header[2] === 0xFF;
    const isPNG = header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4E && header[3] === 0x47;
    const isPDF = header[0] === 0x25 && header[1] === 0x50 && header[2] === 0x44 && header[3] === 0x46; // %PDF
    if (!isJPEG && !isPNG && !isPDF) {
      toast.error("File appears corrupted or is not a valid JPG, PNG, or PDF");
      return;
    }

    setUploading(true);

    try {
      // Get presigned URL for upload
      const presignRes = await fetch(`${API}/upload/presigned-url?file_type=w9&content_type=${encodeURIComponent(file.type)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!presignRes.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { upload_url, file_url } = await presignRes.json();

      // Upload file to S3
      const uploadRes = await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type }
      });

      if (!uploadRes.ok) {
        const errText = await uploadRes.text().catch(() => "");
        console.error("S3 upload failed:", uploadRes.status, errText);
        throw new Error(`Upload failed (${uploadRes.status})`);
      }

      setW9Uploaded(true);
      setW9FileName(file.name);
      toast.success("W9 uploaded successfully");
    } catch (error) {
      console.error("W9 upload error:", error);
      toast.error("Failed to upload W9");
    } finally {
      setUploading(false);
    }
  };

  const handleW9Upload = async (e) => {
    const file = e.target.files?.[0];
    await processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer?.files?.[0];
    await processFile(file);
  };

  const removeW9 = () => {
    setW9Uploaded(false);
    setW9FileName("");
  };

  const togglePaymentMethod = (method) => {
    const existing = paymentMethods.find(pm => pm.method === method);
    if (existing) {
      setPaymentMethods(paymentMethods.filter(pm => pm.method !== method));
    } else {
      setPaymentMethods([...paymentMethods, { method, account_info: "" }]);
    }
  };

  const updatePaymentInfo = (method, accountInfo) => {
    setPaymentMethods(
      paymentMethods.map(pm => 
        pm.method === method ? { ...pm, account_info: accountInfo } : pm
      )
    );
  };

  const isMethodSelected = (method) => {
    return paymentMethods.some(pm => pm.method === method);
  };

  const getAccountInfo = (method) => {
    const pm = paymentMethods.find(p => p.method === method);
    return pm?.account_info || "";
  };

  return (
    <Card className="bg-white/[0.03] border-white/5 p-8 mb-6" data-testid="payments-section">
      <div className="flex items-center gap-3 mb-6">
        <DollarSign className="w-5 h-5 text-white/60" />
        <h2 className="text-xl font-semibold">Payments</h2>
      </div>

      <p className="text-white/50 text-sm mb-6">
        Your W9 and payment information will only be visible to studios <strong className="text-white/80">after</strong> they hire you. 
        Before that, they'll only see that you have payment info on file.
      </p>

      {/* W9 Upload Section */}
      <div className="mb-8">
        <Label className="text-sm text-white/60 uppercase tracking-wider mb-3 block">W9 Form</Label>
        
        {w9Uploaded ? (
          <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
            <FileText className="w-5 h-5 text-green-400" />
            <div className="flex-1">
              <p className="text-green-400 font-medium">W9 on file</p>
              <p className="text-white/50 text-sm">{w9FileName || "w9_document.jpg"}</p>
            </div>
            <Button 
              type="button"
              variant="ghost" 
              size="sm" 
              onClick={removeW9}
              className="text-white/50 hover:text-red-400"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <label 
            className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
              isDragging ? "border-coral bg-coral/10" : "border-white/20 hover:border-white/40"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input 
              type="file" 
              accept="image/jpeg,image/png,application/pdf" 
              onChange={handleW9Upload} 
              className="hidden"
              disabled={uploading}
            />
            {uploading ? (
              <>
                <Loader2 className="w-8 h-8 text-white/40 mb-2 animate-spin" />
                <span className="text-white/60 text-sm">Uploading...</span>
              </>
            ) : (
              <>
                <Upload className={`w-8 h-8 mb-2 ${isDragging ? "text-coral" : "text-white/40"}`} />
                <span className="text-white/60 text-sm">Upload W9 (PDF, JPG, or PNG, max 10MB)</span>
                <span className="text-white/40 text-xs mt-1">Click or drag to upload a scan or photo of your W9</span>
              </>
            )}
          </label>
        )}
      </div>

      {/* Payment Methods Section */}
      <div>
        <Label className="text-sm text-white/60 uppercase tracking-wider mb-3 block">
          <CreditCard className="w-4 h-4 inline mr-2" />
          Preferred Payment Methods
        </Label>
        <p className="text-white/40 text-sm mb-4">Select the payment methods you accept and provide your account details</p>

        <div className="space-y-3">
          {PAYMENT_METHODS.map((pm) => (
            <div key={pm.value} className="space-y-2">
              <div 
                className={`flex items-center gap-3 p-4 bg-white/[0.05] border rounded-sm cursor-pointer transition-all ${
                  isMethodSelected(pm.value) ? "border-white/40 bg-white/5" : "border-white/10 hover:border-white/20"
                }`}
                onClick={() => togglePaymentMethod(pm.value)}
                data-testid={`payment-method-${pm.value}`}
              >
                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-all ${
                  isMethodSelected(pm.value) ? "bg-white border-white" : "border-white/20"
                }`}>
                  {isMethodSelected(pm.value) && <Check className="w-3.5 h-3.5 text-black" />}
                </div>
                <span className="font-medium">{pm.label}</span>
              </div>
              
              {isMethodSelected(pm.value) && (
                <Input
                  value={getAccountInfo(pm.value)}
                  onChange={(e) => updatePaymentInfo(pm.value, e.target.value)}
                  placeholder={pm.placeholder}
                  className="ml-8 bg-white/[0.05] border-white/10 focus:border-white/40 text-white h-10 rounded-sm"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`payment-info-${pm.value}`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
